import { ArrowUpRight, Lock, ShieldCheck } from "lucide-react";
import { motion } from "motion/react";
import type { ReactNode } from "react";
import { Link } from "react-router";
import { Photo } from "@/components/ui/photo";
import { ease } from "@/components/ui/reveal";
import { PROMISES } from "./auth-hero";
import { Logo } from "./logo";

/** An open sign-in section beside the service introduction. */
export function AuthLayout({ title, lead, children }: { title: string; lead?: ReactNode; children: ReactNode }) {
  return (
    <div className="auth-shell">
      <header className="auth-shell-bar">
        <Logo />
        <Link to="/about" className="public-nav-link inline-flex items-center gap-1 text-[13px] font-bold text-ink-2 hover:text-brand">
          サービスの紹介
          <ArrowUpRight className="size-4" />
        </Link>
      </header>

      <main className="auth-shell-body">
        <div className="auth-intro relative isolate hidden overflow-hidden p-10 text-white lg:block">
          <Photo name="hero-office" fill priority sizes="55vw" decorative />
          <div aria-hidden className="absolute inset-0 bg-brand-dark/85" />
          <div className="relative">
          <motion.p initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.9, ease }} className="hero-eyebrow">
            士業の先生のための AI 記事パートナー
          </motion.p>
          <motion.h2 initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1.2, delay: 0.1, ease }} className="hero-title mt-7 max-w-[620px]">
            <span className="inline-block">先生の知見を、</span><br />
            <span className="inline-block">検索で選ばれる</span><span className="inline-block">記事に。</span>
          </motion.h2>
          <motion.p initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1.2, delay: 0.26, ease }} className="mt-7 max-w-[520px] text-[15px] leading-[2] text-slate-200">
            一次資料の確認から、先生の監修、公開後の改善まで。AI が、事務所の記事づくりを一貫して支えます。
          </motion.p>
          <ol className="mt-12 grid max-w-[640px] gap-6 border-t border-white/15 pt-8 sm:grid-cols-3">
            {PROMISES.map((p, i) => (
              <motion.li key={p.title} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.45 + i * 0.12, ease }}>
                <p className="flex items-center gap-2 text-[15px] font-bold text-white">
                  <span className="text-[11px] text-sky-300 tabular-nums">0{i + 1}</span>
                  {p.title}
                </p>
                <p className="mt-1.5 text-[12.5px] leading-relaxed text-slate-300">{p.text}</p>
              </motion.li>
            ))}
          </ol>
          </div>
        </div>

        <motion.section initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 0.2, ease }} className="auth-sheet" aria-labelledby="auth-title">
          <p className="mb-5 text-[1.05rem] leading-snug font-bold text-ink [font-feature-settings:'palt'] lg:hidden">先生の知見を、検索で選ばれる記事に。</p>
          <h1 id="auth-title" className="font-display text-[1.9rem] leading-tight font-semibold text-ink">{title}</h1>
          {lead && <p className="mt-2.5 text-[14px] leading-relaxed text-ink-2">{lead}</p>}
          <div className="mt-7">{children}</div>
          <p className="mt-8 flex items-center gap-1.5 border-t border-line pt-4 text-[12px] text-muted">
            <Lock className="size-3.5 shrink-0" />
            招待制のサービスです。ログインには二段階認証が必要です。
          </p>
        </motion.section>
      </main>

      <footer className="auth-shell-foot">
        <span className="inline-flex items-center gap-1.5"><ShieldCheck className="size-3.5" />二段階認証と通信の暗号化で保護</span>
        <span>
          © {new Date().getFullYear()} Expert Column AI
          <span className="mx-2 text-line-strong">｜</span>
          <Link to="/credits" className="hover:text-brand hover:underline">画像の出典</Link>
        </span>
      </footer>
    </div>
  );
}

/** The footer of the signed-in screens and the error page. */
export function Footer() {
  return (
    <footer className="border-t border-line bg-transparent p-4 text-center text-xs text-muted">
      <span className="font-display hidden sm:inline">専門家の知見を、届く言葉に。</span>
      <span className="mx-2 hidden text-line-strong sm:inline">｜</span>© {new Date().getFullYear()} Expert Column AI
      <span className="mx-2 text-line-strong">｜</span>
      <Link to="/credits" className="hover:text-brand hover:underline">画像の出典</Link>
    </footer>
  );
}
